//外部依赖
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
//内部组件和方法
import CDNImage from '@/components/cdnImage/cdnImage';
import JDIPrice from '@/components/price/index';
import SafeLink from '@/components/SafeLink';
import UKProductSelDrawer from '@/components/UKProductSelDrawer';
import { formatProductLink } from '@/utils/formatProductLink';
import { gotoDp } from '@/utils/goto';
import {
  comparePrice,
  formatStyle,
  getExptag,
  imgTipsStyle,
  paramsLoggerError,
} from '../../common/utils';
//本地图片
import AddCartIcon from '@/assets/icons/AddCart.svg';
import AddCartGreyIcon from '@/assets/icons/AddCartGrey.svg';
//接口和ts
import { addCartUtil, CartResProps } from '@/utils/cartUtils';
import type { productData_1 } from '../../index.interface';
import { CartIconColorType, HoverSkuNameColorType } from '../../index.interface';
//多语言
import { useTranslation } from '@/i18n/client';
//样式
import {
  PRODUCT_CARD_EVENT_NAMES,
  PRODUCT_CARD_TYPE,
  productCardBusinessLogger,
} from '@/utils/logger/instance/productCardBusinessLogger';
import style from './style.module.scss';
import { useStores } from '@/models';

interface PropsType {
  productData: productData_1;
}

export default function ProductCard_1({ productData }: PropsType) {
  let {
    from = '',
    width,
    height,
    containerStyle,
    shouldShowSkuName = true,
    skuName,
    skuNameStyle,
    skuImgKeyParams,
    skuImg,
    skuImgStyle,
    skuId,
    shouldShowPrice = true,
    priceStyle,
    realPrice,
    realPriceJDIPriceParams,
    originalPrice,
    originalPriceJDIPriceParams,
    currency,
    languageCode,
    imgTips,
    shouldShowCart,
    onClickCart,
    onAddCartSuccess,
    jumpTarget,
    jumpUrl,
    onClickProduct,
    saleTipsDom,
    shouldShowMarketing = true,
    marketingStyle,
    marketingArray,
    isOutOfStock,
    isRemoved,
    trackingPointParams,
    shipmentType,
    shouldShowModal,
    shouldShowService = false,
    brandName,
    cartIconColor,
    isUnderline,
    isCartIconGreyHover,
    hoverSkuNameColor = HoverSkuNameColorType.RED,
  } = productData;

  containerStyle = Object.assign({}, containerStyle);
  if (width !== undefined && width !== null) {
    containerStyle.width = formatStyle(width);
  }
  if (height !== undefined && height !== null) {
    containerStyle.height = formatStyle(height);
  }

  const { t } = useTranslation();
  const { cartNumStore } = useStores();
  const router = useRouter();
  const [isShowProductModal, setIsShowProductModal] = useState(false); // 是否显示选择商品类型弹窗
  useEffect(() => {
    if (paramsLoggerError(productData, PRODUCT_CARD_TYPE.TYPE_1)) {
      const { params_error, message }: any = paramsLoggerError(
        productData,
        PRODUCT_CARD_TYPE.TYPE_1,
      );
      //sgm监控，商卡入参错误
      productCardBusinessLogger({
        eventName: PRODUCT_CARD_EVENT_NAMES.PARAMS_ERROR,
        message,
        category: {
          params_error,
        },
        status: false,
      });
    }
  }, [productData]);
  //点击商卡事件
  const handleProductClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    //sgm监控，商卡点击事件
    productCardBusinessLogger({
      eventName: PRODUCT_CARD_EVENT_NAMES.PRODUCT_CARD_CLICK,
      message: {
        from,
        type: PRODUCT_CARD_TYPE.TYPE_1,
        mainSkuId: skuId,
        jumpUrl: jumpUrl ? jumpUrl : skuImg || '',
      },
    });
    if (trackingPointParams && trackingPointParams.productClickPoint) {
      trackingPointParams.productClickPoint();
    }
    // 检查 onClickCart 是否存在并且是一个函数
    if (onClickProduct && typeof onClickProduct === 'function') {
      onClickProduct(productData);
    } else {
      //如果不加这个setTimeout的话，上面的埋点和sgm就没法全都执行
      if (jumpUrl) {
        setTimeout(() => {
          window.location.href = jumpUrl;
        }, 100);
      } else {
        router.push(formatProductLink(skuId, skuName));
      }
    }
  };
  //点击购物车事件
  const handleCartClick = (e: any, productData: productData_1) => {
    e.preventDefault();
    e.stopPropagation();

    //sgm监控，加车点击事件
    productCardBusinessLogger({
      eventName: PRODUCT_CARD_EVENT_NAMES.ADD_CART_BUTTON_CLICK,
      message: {
        from,
        type: PRODUCT_CARD_TYPE.TYPE_1,
        mainSkuId: skuId,
      },
    });

    if (trackingPointParams && trackingPointParams.cartClickPoint) {
      trackingPointParams.cartClickPoint();
    }
    // 检查 onClickCart 是否存在并且是一个函数
    if (onClickCart && typeof onClickCart === 'function') {
      onClickCart(productData);
    } else {
      //加车时是否展示规格弹层
      if (shouldShowModal) {
        setIsShowProductModal(true);
      } else {
        //直接加车
        loadAddCart({ skuId });
      }
    }
  };

  /**
   * 加车
   * https://cf.jd.com/pages/viewpage.action?pageId=605887939
   */
  const loadAddCart = async ({ skuId }: any) => {
    addCartUtil([{ skuId, num: '1' }], false, shipmentType).then((res: CartResProps) => {
      if (trackingPointParams && trackingPointParams.addCartPoint) {
        trackingPointParams.addCartPoint();
      }
      if (res?.result?.code === '200') {
        // 更新购物车数量
        cartNumStore.updateCartNum(res?.result?.data);
      }
      //加车接口调用成功回调方法
      if (onAddCartSuccess && typeof onAddCartSuccess === 'function') onAddCartSuccess(productData);
    });
  };

  const handleClose = () => {
    setIsShowProductModal(false);
  };

  return (
    <div
      className={`sgm_pc ${style.UK_product_card} ${isUnderline ? style.show_underline : ''}`}
      style={containerStyle}
      key={skuId}
      exptag={getExptag('exptag', trackingPointParams, 'productExpConfig')}
      data-exptag-config={getExptag('config', trackingPointParams, 'productExpConfig')}
      data-exptag-json-param={getExptag('json-param', trackingPointParams, 'productExpConfig')}
      data-exptag-event-param={getExptag('event-param', trackingPointParams, 'productExpConfig')}
    >
      <SafeLink
        href={jumpUrl ? jumpUrl : formatProductLink(skuId, skuName)}
        target={jumpTarget}
        onClick={(e) => handleProductClick(e)}
      >
        {/* 商品图片 */}
        <div className={style.UK_image_wrapper}>
          <CDNImage
            className={`${style.UK_skuImg} ${isOutOfStock || isRemoved ? style.op50 : ''}`}
            src={(skuImgKeyParams ? skuImgKeyParams?.imgUrlKey : skuImg) as string}
            style={skuImgStyle}
            width={skuImgKeyParams?.width || 240}
            height={skuImgKeyParams?.height || 240}
            alt={skuName}
          />
          {/* <div className={style.UK_mask}></div> */}
          {/* 是否展示无货状态 */}
          {isRemoved ? (
            <div className={style.UK_outOfStock}>
              <div className={style.UK_outOfStock_text}>{t('common.productStatus.removed')}</div>
            </div>
          ) : isOutOfStock ? (
            <div className={style.UK_outOfStock}>
              <div className={style.UK_outOfStock_text}>{t('common.productStatus.outOfStock')}</div>
            </div>
          ) : null}
          {imgTips && imgTips.tipDom ? (
            <span className={style.UK_imgTips} style={imgTipsStyle(imgTips.position)}>
              {imgTips.tipDom}
            </span>
          ) : null}
        </div>
        {/* 商品名称 */}
        {shouldShowSkuName && (
          <div className={style.UK_name_wrapper}>
            {/* 折扣标签 */}
            {saleTipsDom ? <div className={style.UK_saleTipsDom}>{saleTipsDom}</div> : null}
            <span
              className={`${style.UK_skuName} ${style[hoverSkuNameColor]}`}
              style={skuNameStyle}
            >
              {brandName && (
                <>
                  <span className={style.Uk_brandsName}>{brandName}</span>
                  <span className={style.UK_Divider}>|</span>
                </>
              )}
              <span className={style.UK_name}>{skuName}</span>
            </span>
          </div>
        )}
      </SafeLink>

      {/* 商品价格+ 购物车 */}
      {shouldShowPrice && (
        <div className={style.UK_price_and_cart_wrapper}>
          <div className={style.UK_left}>
            <div className={style.UK_price} style={priceStyle}>
              <JDIPrice
                {...realPriceJDIPriceParams}
                locale={languageCode}
                currency={currency || ''}
                // @ts-ignore
                price={realPrice}
                uiType={1}
                containerStyle={{
                  marginRight: '8px',
                  ...realPriceJDIPriceParams?.containerStyle,
                }}
              />
              {originalPrice && realPrice && comparePrice(originalPrice, realPrice) && (
                <JDIPrice
                  {...originalPriceJDIPriceParams}
                  locale={languageCode}
                  currency={currency || ''}
                  price={originalPrice}
                  uiType={2}
                  lineThrough
                />
              )}
            </div>
          </div>
          {shouldShowCart && !isRemoved ? (
            <div
              className={style.UK_right}
              exptag={getExptag('exptag', trackingPointParams, 'cartExpConfig')}
              data-exptag-config={getExptag('config', trackingPointParams, 'cartExpConfig')}
              data-exptag-json-param={getExptag('json-param', trackingPointParams, 'cartExpConfig')}
              data-exptag-event-param={getExptag(
                'event-param',
                trackingPointParams,
                'cartExpConfig',
              )}
            >
              {cartIconColor === CartIconColorType.BLACK ? (
                <AddCartGreyIcon
                  className={`${style.UK_cart} ${
                    isCartIconGreyHover ? style.UK_cart_grey_hover : ''
                  }`}
                  onClick={(e: any) => handleCartClick(e, productData)}
                ></AddCartGreyIcon>
              ) : (
                <AddCartIcon
                  className={style.UK_cart}
                  onClick={(e: any) => handleCartClick(e, productData)}
                ></AddCartIcon>
              )}
            </div>
          ) : null}
        </div>
      )}
      {/* 底部营销标签 */}
      {shouldShowMarketing && marketingArray && marketingArray.length > 0
        ? marketingArray.map((item, i) => {
            return (
              <div className={style.UK_activityRow} key={i} style={marketingStyle}>
                {item}
              </div>
            );
          })
        : null}
      <UKProductSelDrawer
        skuId={skuId}
        triggerOpen={isShowProductModal}
        handleClose={handleClose}
        updateItemData={loadAddCart}
        isShowService={shouldShowService}
      />
    </div>
  );
}
